import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useConfiguration } from '../../../../context/configurationContext';
import { buildSearchQuery } from '../../../../util/urlHelpers';
import { NamedLink } from '../../../../components';
import DropdownMenu from './DropdownMenu/DropdownMenu';

import css from './TopbarNavigation.module.css';

/**
 * Custom hook to manage hover state and category levels.
 */
const useHoverState = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredCategoryLevel, setHoveredCategoryLevel] = useState({
    categoryLevel1: null,
    categoryLevel2: null,
    categoryLevel3: null,
  });

  const handleMouseEnter = useCallback(() => setIsDropdownOpen(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsDropdownOpen(false);
    setHoveredCategoryLevel({
      categoryLevel1: null,
      categoryLevel2: null,
      categoryLevel3: null,
    });
  }, []);

  const handleCategoryHover = useCallback(categoryId => {
    setHoveredCategoryLevel(prevState => ({
      ...prevState,
      categoryLevel1: categoryId,
      categoryLevel2: null,
      categoryLevel3: null,
    }));
  }, []);

  return {
    isDropdownOpen,
    hoveredCategoryLevel,
    setHoveredCategoryLevel,
    handleMouseEnter,
    handleMouseLeave,
    handleCategoryHover,
  };
};

const TopbarNavigation = props => {
  const { className, rootClassName, config, location } = props;
  const { categories: listingCategoriesConfig } = config.categoryConfiguration || {};
  const classes = classNames(rootClassName || css.root, className);

  const {
    isDropdownOpen,
    hoveredCategoryLevel,
    setHoveredCategoryLevel,
    handleMouseEnter,
    handleMouseLeave,
    handleCategoryHover,
  } = useHoverState();

  const navigationItems = useMemo(
    () =>
      listingCategoriesConfig?.map(category => (
        <li
          key={category.id}
          className={classNames(css.navigationItem, {
            [css.active]: hoveredCategoryLevel.categoryLevel1 === category.id,
          })}
          onMouseEnter={() => handleCategoryHover(category.id)}
        >
          <NamedLink
            className={css.navigationLink}
            name="SearchPage"
            // The link's own category, not the hovered one: with keyboard or touch
            // nothing is hovered and every top-level link went to plain /products.
            to={{ search: buildSearchQuery({ categoryLevel1: category.id }, null, null, location) }}
          >
            {category.name}
          </NamedLink>
        </li>
      )),
    [listingCategoriesConfig, hoveredCategoryLevel.categoryLevel1, handleCategoryHover, location.search]
  );

  return (
    <div className={classes} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <ul className={css.navigationItems}>{navigationItems}</ul>
      {isDropdownOpen && hoveredCategoryLevel.categoryLevel1 && (
        <DropdownMenu
          location={location}
          categories={listingCategoriesConfig}
          hoveredCategoryLevel={hoveredCategoryLevel}
          setHoveredCategoryLevel={setHoveredCategoryLevel}
        />
      )}
    </div>
  );
};

TopbarNavigation.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

const EnhancedTopbarNavigation = props => {
  const config = useConfiguration();
  const location = useLocation();

  return <TopbarNavigation config={config} location={location} {...props} />;
};

export default EnhancedTopbarNavigation;
